import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
    useEditController,
} from 'react-admin';
import { Box, Card, CardContent, Typography, IconButton } from '@mui/material';
import { ArrowBack, EmojiTransportation } from '@mui/icons-material';
import { BrandDeleteWithConfirmButton } from './BrandDeleteWithConfirmButton';

const transformBrandSubmission = (data: any) => {
    const translations: Record<string, string> = {};
    if (data.translation_en !== undefined) translations.en = data.translation_en;
    if (data.translation_zh_tw !== undefined) translations['zh-TW'] = data.translation_zh_tw;
    if (data.translation_zh_cn !== undefined) translations['zh-CN'] = data.translation_zh_cn;
    return { translations };
};

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': { backgroundColor: '#00A843' },
            }}
        />
        <BrandDeleteWithConfirmButton />
    </Toolbar>
);

const PageHeader = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/vehicle-brands')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.vehicle-brands.edit.title', { _: 'Edit Vehicle Brand' })}
                </Typography>
            </Box>
        </Box>
    );
};

const FlattenRecord: React.FC = () => {
    const { record } = useEditController();
    if (!record) return null;
    const flattened = {
        ...record,
        translation_en: (record.translations as any)?.en || '',
        translation_zh_tw: (record.translations as any)?.['zh-TW'] || '',
        translation_zh_cn: (record.translations as any)?.['zh-CN'] || '',
    };
    return <FormBody record={flattened} />;
};

const FormBody: React.FC<{ record: any }> = ({ record }) => {
    const translate = useTranslate();
    return (
        <SimpleForm
            toolbar={<EditToolbar />}
            record={record}
            defaultValues={record}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                <PageHeader />
                <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <EmojiTransportation sx={{ mr: 2, color: '#00C853' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {translate('resources.vehicle-brands.sections.translations', {
                                    _: 'Brand name (per language)',
                                })}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <TextInput
                                source="normalized_name"
                                label={translate('resources.vehicle-brands.fields.normalizedName', {
                                    _: 'Normalized Name',
                                })}
                                disabled
                                fullWidth
                                helperText={translate('resources.vehicle-brands.help.normalizedName', {
                                    _: 'Read-only: derived from English name on create',
                                })}
                            />
                            <TextInput
                                source="translation_en"
                                label={translate('resources.vehicle-brands.fields.translationEn', {
                                    _: 'English Name',
                                })}
                                validate={required()}
                                fullWidth
                            />
                            <TextInput
                                source="translation_zh_tw"
                                label={translate('resources.vehicle-brands.fields.translationZhTw', {
                                    _: '繁體中文 Name',
                                })}
                                fullWidth
                            />
                            <TextInput
                                source="translation_zh_cn"
                                label={translate('resources.vehicle-brands.fields.translationZhCn', {
                                    _: '简体中文 Name',
                                })}
                                fullWidth
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </SimpleForm>
    );
};

export const VehicleBrandsEdit = () => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            transform={transformBrandSubmission}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.vehicle-brands.notifications.updated', {
                        type: 'success',
                        messageArgs: { _: 'Brand updated' },
                    });
                    redirect('list', 'vehicle-brands');
                },
                onError: (error: any) => {
                    notify(error?.body?.error || error?.message || 'Failed to update brand', {
                        type: 'error',
                    });
                },
            }}
        >
            <FlattenRecord />
        </Edit>
    );
};
